import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { Content, Header, Page } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityDisplayName,
} from '@backstage/plugin-catalog-react';
import { AppsEnv, LlmProxyKeyListItem } from '@internal/plugin-llm-proxy-keys-common';
import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useLlmProxyKeysApi, useLlmProxyKeysConfig } from '../hooks';
import { KeyRevealDialog } from './KeyRevealDialog';

type AppsKeysPageProps = {
  embedded?: boolean;
};

const DEFAULT_KEY_NAME_SUFFIX = 'main';

export const AppsKeysPage = ({ embedded = false }: AppsKeysPageProps) => {
  const api = useLlmProxyKeysApi();
  const catalogApi = useApi(catalogApiRef);
  const { config } = useLlmProxyKeysConfig();
  const environments = config?.appsEnvironments ?? [];
  const [env, setEnv] = useState<AppsEnv | ''>('');
  const selectedEnv = (env || environments[0] || 'prod') as AppsEnv;
  const [reloadTick, setReloadTick] = useState(0);

  const { value: keys, loading, error } = useAsync(
    () => (environments.length ? api.listAppsKeys(selectedEnv) : Promise.resolve([])),
    [api, selectedEnv, environments.length, reloadTick],
  );

  const {
    value: components = [],
    loading: componentsLoading,
    error: componentsError,
  } = useAsync(
    async () => {
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'component' },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'spec.system',
        ],
      });
      return items.sort((a, b) =>
        stringifyEntityRef(a).localeCompare(stringifyEntityRef(b)),
      );
    },
    [catalogApi],
  );

  const [component, setComponent] = useState<Entity | null>(null);
  const [keyNameSuffix, setKeyNameSuffix] = useState('');
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState<{ title: string; key: string }>();

  const keyNamePrefix = component ? `${component.metadata.name}-` : '';

  const runCreate = async () => {
    if (!component || !keyNameSuffix.trim()) {
      return;
    }
    setBusy(true);
    try {
      const result = await api.createAppsKey({
        env: selectedEnv,
        componentName: component.metadata.name,
        keyName: keyNameSuffix.trim(),
      });
      setReveal({ title: 'API key created', key: result.key });
      setKeyNameSuffix(DEFAULT_KEY_NAME_SUFFIX);
      setReloadTick(tick => tick + 1);
    } finally {
      setBusy(false);
    }
  };

  const runRotate = async (name: string) => {
    setBusy(true);
    try {
      const result = await api.rotateAppsKey({
        env: selectedEnv,
        name,
      });
      setReveal({ title: 'API key rotated', key: result.key });
      setReloadTick(tick => tick + 1);
    } finally {
      setBusy(false);
    }
  };

  const runRevoke = async (name: string) => {
    setBusy(true);
    try {
      await api.revokeAppsKey(selectedEnv, name);
      setReloadTick(tick => tick + 1);
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <>
      {!environments.length && (
        <Alert severity="info">No apps environments are configured.</Alert>
      )}
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{String(error)}</Alert>}
      {environments.length > 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl variant="outlined" style={{ minWidth: 200 }}>
              <InputLabel id="llm-proxy-apps-env-label">Environment</InputLabel>
              <Select
                labelId="llm-proxy-apps-env-label"
                label="Environment"
                value={selectedEnv}
                onChange={event => setEnv(event.target.value as AppsEnv)}
              >
                {environments.map((environment: AppsEnv) => (
                  <MenuItem key={environment} value={environment}>
                    {environment}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader title="Create apps key" />
              <CardContent>
                {componentsError && (
                  <Alert severity="warning" style={{ marginBottom: 16 }}>
                    Could not load catalog components: {String(componentsError)}.
                    You can still manage existing keys below.
                  </Alert>
                )}
                <Autocomplete
                  options={components}
                  loading={componentsLoading}
                  value={component}
                  onChange={(_event, value) => {
                    setComponent(value);
                    setKeyNameSuffix(value ? DEFAULT_KEY_NAME_SUFFIX : '');
                  }}
                  getOptionLabel={entity => stringifyEntityRef(entity)}
                  renderOption={entity => (
                    <EntityDisplayName entityRef={stringifyEntityRef(entity)} />
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Component"
                      margin="normal"
                      fullWidth
                    />
                  )}
                />
                <TextField
                  label="Key name"
                  helperText={
                    component
                      ? `Stored as ${keyNamePrefix}{your key name}`
                      : 'Select a component first'
                  }
                  value={keyNameSuffix}
                  onChange={event => setKeyNameSuffix(event.target.value)}
                  margin="normal"
                  fullWidth
                  disabled={!component}
                  InputProps={{
                    startAdornment: component ? (
                      <InputAdornment position="start">
                        <Typography color="textPrimary" component="span">
                          {keyNamePrefix}
                        </Typography>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={runCreate}
                  disabled={busy || !component || !keyNameSuffix.trim()}
                >
                  Create key
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardHeader title={`Keys in ${selectedEnv}`} />
              <CardContent>
                {!keys?.length && (
                  <Typography color="textSecondary">No keys yet.</Typography>
                )}
                {keys?.map((item: LlmProxyKeyListItem) => (
                  <div key={item.name} style={{ marginBottom: 16 }}>
                    <Typography>
                      <strong>{item.name}</strong> (user_id: {item.userId})
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => runRotate(item.name)}
                      disabled={busy}
                      style={{ marginRight: 8 }}
                    >
                      Rotate
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => runRevoke(item.name)}
                      disabled={busy}
                    >
                      Revoke
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {reveal && (
        <KeyRevealDialog
          open
          title={reveal.title}
          apiKey={reveal.key}
          message="Copy this key now and send it to the component owner. It will not be shown again."
          onClose={() => setReveal(undefined)}
        />
      )}
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Page themeId="tool">
      <Header title="LLM Proxy Keys (Apps)" />
      <Content>{body}</Content>
    </Page>
  );
};
